import {
  Building2,
  ExternalLink,
  Languages,
  MessageSquare,
  MessageSquareText,
  Percent,
  Tags,
  ThumbsDown,
  Users,
} from 'lucide-react';
import type {
  FeedbackAnnouncement,
  FeedbackCommentView,
  FeedbackSubject,
  FeedbackSummary,
} from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Card } from '../../shared/ui/Card.js';
import { ExportMenu } from '../../shared/ui/ExportMenu.js';
import { MiniBar } from '../../shared/ui/MiniBar.js';
import { ReadingList, type ReadingRow } from '../../shared/ui/ReadingList.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { StatCard } from '../../shared/ui/StatCard.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import { useFeedbackSummary } from '../../shared/api/queries.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import { useAnalyticsFilters } from '../../shared/api/useAnalyticsFilters.js';
import { FeedbackFilterBar } from './FeedbackFilterBar.js';
import './feedback.css';

const UNKNOWN_ENTITY = 'Non renseignée par le portail';
const UNKNOWN_TYPE = 'Type non renseigné';
const LANGUAGE_LABELS: Record<string, string> = {
  fr: 'Français',
  ar: 'Arabe',
  zgh: 'Amazighe',
  unknown: 'Non renseignée',
};
/** Above this demand score an announcement is worth a look (same threshold as the bars). */
const DEMAND_ALERT = 50;
/** Announcements the portal names by a generated identifier rather than a readable key. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHORT_ID_LENGTH = 8;

const count = (value: number) => value.toLocaleString('fr-FR');
const percent = (ratio: number) => `${(ratio * 100).toFixed(1).replace('.', ',')} %`;
const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A generated identifier is shortened; a key the site chose ("concours-attache-2026") reads as it is. */
function announcementName(key: string): string {
  return UUID.test(key) ? `Annonce ${key.slice(0, SHORT_ID_LENGTH)}` : key;
}

/** The answers that count as negative, in the words the visitor read: « Non ». */
function negativeWords(summary: FeedbackSummary | undefined): string {
  const words = new Set<string>();
  for (const subject of summary?.byAnnouncement ?? []) {
    for (const answer of subject.answers) if (answer.negative) words.add(answer.label ?? answer.answer);
  }
  return words.size === 0 ? 'négatives' : [...words].map((word) => `« ${word} »`).join(', ');
}

/** Answer counts as compact chips: the label the visitor read and its count, negative answers marked. */
function AnswerChips({ subject }: { subject: FeedbackSubject }) {
  return (
    <span className="answers">
      {[...subject.answers]
        .sort((a, b) => Number(a.negative) - Number(b.negative))
        .map((answer) => (
          <span
            key={answer.answer}
            className={`answers__chip ${answer.negative ? 'answers__chip--negative' : ''}`}
            title={answer.negative ? 'Réponse négative' : 'Réponse positive ou neutre'}
          >
            {answer.label ?? answer.answer} · {count(answer.count)}
          </span>
        ))}
    </span>
  );
}

const commentDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** What a visitor wrote, title first; the text keeps its line breaks. */
function CommentCell({ comment }: { comment: FeedbackCommentView }) {
  return (
    <div className="comment-cell" dir="auto">
      {comment.title && <div className="cell-strong">{comment.title}</div>}
      {comment.body && <p className="comment-cell__body">{comment.body}</p>}
    </div>
  );
}

function DemandCell({ subject }: { subject: FeedbackSubject }) {
  return (
    <div className="cell-demand">
      <MiniBar
        value={subject.demandScore / 100}
        tone={subject.demandScore >= DEMAND_ALERT ? 'negative' : 'brand'}
      />
      <span>{subject.demandScore}</span>
      {subject.lowConfidence && <Badge tone="warning">Peu d'avis</Badge>}
    </div>
  );
}

/**
 * "Avis recueillis" (functional §5.1, §5.8): the voice of the user restituted
 * by issuing entity, by announcement and by announcement type, with the
 * participation rate and the demand score of functional §5.5. Both roles.
 * Every figure says what it counts, for someone who opens the screen for the first time.
 */
export function FeedbackPage() {
  const { project } = useActiveProject();
  const analyticsFilters = useAnalyticsFilters();
  const { filters, queryString } = analyticsFilters;
  const { data, isLoading, isFetching } = useFeedbackSummary(project?.id, queryString);
  const portal = project?.primaryUrl?.replace(/\/$/, '') ?? null;
  const portalLink = (row: FeedbackAnnouncement) =>
    portal && row.pagePath ? `${portal}${row.pagePath}` : null;
  const question = data?.questions[0]?.question;
  const negatives = negativeWords(data);

  const entityColumns: Array<Column<FeedbackSubject>> = [
    {
      key: 'entity',
      header: 'Administration émettrice',
      render: (row) =>
        row.key === null ? (
          <span className="cell-sub">{UNKNOWN_ENTITY}</span>
        ) : (
          <button
            type="button"
            className="link-cell"
            onClick={() => analyticsFilters.set({ entityKey: row.key ?? undefined })}
          >
            {row.key}
          </button>
        ),
    },
    { key: 'responses', header: 'Avis', align: 'end', render: (row) => count(row.responses) },
    {
      key: 'negative',
      header: 'Réponses négatives',
      align: 'end',
      render: (row) => percent(row.negativeShare),
    },
    { key: 'answers', header: 'Réponses', render: (row) => <AnswerChips subject={row} /> },
    { key: 'demand', header: 'Score de demande', render: (row) => <DemandCell subject={row} /> },
  ];

  const commentColumns: Array<Column<FeedbackCommentView>> = [
    {
      key: 'announcement',
      header: 'Annonce',
      width: '18%',
      render: (row) => (
        <div>
          <div className="cell-strong" title={row.announcementKey}>
            {announcementName(row.announcementKey)}
          </div>
          <div className="cell-sub">{row.entityKey ?? UNKNOWN_ENTITY}</div>
        </div>
      ),
    },
    {
      key: 'answer',
      header: 'Réponse choisie',
      width: '20%',
      render: (row) => (
        <span className={`answers__chip ${row.negative ? 'answers__chip--negative' : ''}`}>
          {row.answerLabel ?? row.answer}
        </span>
      ),
    },
    { key: 'comment', header: 'Commentaire', render: (row) => <CommentCell comment={row} /> },
    { key: 'date', header: 'Reçu le', width: '14%', render: (row) => commentDate(row.occurredAt) },
  ];

  const announcementColumns: Array<Column<FeedbackAnnouncement>> = [
    {
      key: 'announcement',
      header: 'Annonce',
      render: (row) => {
        const link = portalLink(row);
        return (
          <div>
            <div className="cell-strong" title={row.key}>
              {announcementName(row.key)}
            </div>
            <div className="cell-sub">
              {row.entityKey ?? UNKNOWN_ENTITY}
              {link && (
                <>
                  {' · '}
                  <a className="feedback__portal-link" href={link} target="_blank" rel="noopener noreferrer">
                    Voir sur le portail <ExternalLink size={11} aria-hidden />
                  </a>
                </>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) =>
        row.announcementType ? (
          <Badge tone="teal">{capitalize(row.announcementType)}</Badge>
        ) : (
          <span className="cell-sub">{UNKNOWN_TYPE}</span>
        ),
    },
    { key: 'responses', header: 'Avis', align: 'end', render: (row) => count(row.responses) },
    {
      key: 'negative',
      header: 'Réponses négatives',
      align: 'end',
      render: (row) => percent(row.negativeShare),
    },
    { key: 'answers', header: 'Réponses', render: (row) => <AnswerChips subject={row} /> },
    { key: 'demand', header: 'Score de demande', render: (row) => <DemandCell subject={row} /> },
  ];

  const typeColumns: Array<Column<FeedbackSubject>> = [
    {
      key: 'type',
      header: "Type d'annonce",
      render: (row) =>
        row.key === null ? (
          <span className="cell-sub">{UNKNOWN_TYPE}</span>
        ) : (
          <button
            type="button"
            className="link-cell"
            onClick={() => analyticsFilters.set({ announcementType: row.key ?? undefined })}
          >
            {capitalize(row.key)}
          </button>
        ),
    },
    { key: 'responses', header: 'Avis', align: 'end', render: (row) => count(row.responses) },
    {
      key: 'negative',
      header: 'Réponses négatives',
      align: 'end',
      render: (row) => percent(row.negativeShare),
    },
    { key: 'demand', header: 'Score de demande', render: (row) => <DemandCell subject={row} /> },
  ];

  const periodLabel = data
    ? `${new Date(data.period.from).toLocaleDateString('fr-FR')} → ${new Date(data.period.to).toLocaleDateString('fr-FR')}`
    : undefined;
  const scopeLabel = [filters.entityKey, filters.announcementType].filter(Boolean).join(' · ');
  const unknownEntityOnly = (data?.byEntity ?? []).every((row) => row.key === null);

  return (
    <>
      <PageHeader
        title="Avis recueillis"
        description={
          project
            ? `Ce que les usagers disent des annonces · Projet ${project.name}${periodLabel ? ` · ${periodLabel}` : ''}${scopeLabel ? ` · ${scopeLabel}` : ''}`
            : undefined
        }
        actions={<ExportMenu projectId={project?.id} queryString={queryString} />}
      />

      <FeedbackFilterBar filters={analyticsFilters} dimensions={data?.dimensions} />

      {data && data.responses > 0 && (
        <FeedbackReading summary={data} question={question} negatives={negatives} portalLink={portalLink} />
      )}

      <div className="feedback__kpis">
        <StatCard
          icon={<MessageSquare />}
          label="Avis recueillis"
          value={count(data?.responses ?? 0)}
          definition={
            question
              ? `Réponses à la question « ${question} », posée sur les pages d'annonce.`
              : "Réponses données à la question posée sur les pages d'annonce."
          }
          lowConfidence={data?.lowConfidence}
        />
        <StatCard
          icon={<Percent />}
          label="Taux de participation"
          value={percent(data?.participationRate ?? 0)}
          definition={`Part des usagers qui ont répondu quand la question leur a été posée (${count(data?.promptsShown ?? 0)} fois).`}
          lowConfidence={data?.lowConfidence}
        />
        <StatCard
          icon={<ThumbsDown />}
          label="Avis négatifs"
          value={percent(data?.negativeShare ?? 0)}
          definition={`Part des réponses ${negatives} : l'annonce a paru peu claire à l'usager.`}
          lowConfidence={data?.lowConfidence}
        />
        <StatCard
          icon={<Users />}
          label="Score de demande"
          value={String(data?.demandScore ?? 0)}
          definition={`De 0 à 100 : monte avec le nombre d'avis et la part de réponses négatives. Au-delà de ${DEMAND_ALERT}, à revoir.`}
          lowConfidence={data?.lowConfidence}
        />
        {(isLoading || isFetching) && (
          <p className="feedback__loading" data-testid="feedback-loading">
            Chargement des avis…
          </p>
        )}
      </div>

      <Card
        title="Par annonce"
        icon={<MessageSquare size={15} />}
        padded={false}
        className="feedback__section"
      >
        <Table
          columns={announcementColumns}
          rows={data?.byAnnouncement ?? []}
          rowKey={(row) => row.key}
          empty="Aucun avis recueilli sur la période."
        />
        <p className="feedback__note">
          Les annonces sont classées par nombre d'avis. Une annonce est nommée par l'identifiant que le
          portail lui donne dans son adresse ; « Voir sur le portail » ouvre la page lue par l'usager. « Peu
          d'avis » : moins de {data?.minimumSessionCount ?? '…'} réponses, le chiffre est indicatif.
        </p>
      </Card>

      <Card
        title="Commentaires des usagers"
        icon={<MessageSquareText size={15} />}
        padded={false}
        className="feedback__section"
      >
        <Table
          columns={commentColumns}
          rows={data?.comments ?? []}
          rowKey={(row) => `${row.occurredAt} ${row.announcementKey} ${row.answer}`}
          empty="Aucun commentaire sur la période."
        />
        <p className="feedback__note">
          Les {data?.comments.length ?? 0} derniers commentaires, du plus récent au plus ancien, sous les
          mêmes filtres. L'usager les écrit après avoir dit ce qui le bloque ; les numéros, adresses et
          identifiants qu'il aurait laissés sont masqués (•••) avant d'arriver ici. Ils permettent de
          transmettre à chaque administration les remarques sur ses propres annonces.
        </p>
      </Card>

      <Card
        title="Par administration émettrice"
        icon={<Building2 size={15} />}
        padded={false}
        className="feedback__section"
      >
        <Table
          columns={entityColumns}
          rows={data?.byEntity ?? []}
          rowKey={(row) => row.key ?? 'unknown'}
          empty="Aucun avis recueilli sur la période."
        />
        <p className="feedback__note">
          {unknownEntityOnly
            ? "Le portail n'indique pas encore quelle administration publie chaque annonce. Elle apparaîtra ici dès que la page d'annonce portera le marqueur prévu à l'intégration ; chaque administration pourra alors suivre les avis sur ses propres annonces."
            : "Les avis sont regroupés par administration qui publie l'annonce, pour lui être transmis (ici et par export). Cliquez sur une administration pour ne voir que ses avis."}
        </p>
      </Card>

      <div className="feedback__charts">
        <Card title="Par type d'annonce" icon={<Tags size={15} />} padded={false}>
          <Table
            columns={typeColumns}
            rows={data?.byAnnouncementType ?? []}
            rowKey={(row) => row.key ?? 'unknown'}
            empty="Aucun avis recueilli sur la période."
          />
        </Card>

        <Card title="Par langue d'affichage" icon={<Languages size={15} />}>
          {(data?.byLanguage ?? []).length === 0 ? (
            <p className="feedback__empty">Aucun avis recueilli sur la période.</p>
          ) : (
            <dl className="feedback__languages">
              {(data?.byLanguage ?? []).map((entry) => (
                <div key={entry.language} data-language={entry.language}>
                  <dt>{LANGUAGE_LABELS[entry.language] ?? entry.language}</dt>
                  <dd>
                    {count(entry.responses)} avis · {percent(entry.negativeShare)} négatifs
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>

      {data && (
        <p className="feedback__note feedback__note--page">
          Un usager donne au plus un avis par annonce. Aucune donnée personnelle et aucun commentaire libre ne
          sont recueillis : seulement la réponse choisie.
        </p>
      )}
    </>
  );
}

interface FeedbackReadingProps {
  readonly summary: FeedbackSummary;
  readonly question: string | undefined;
  readonly negatives: string;
  readonly portalLink: (row: FeedbackAnnouncement) => string | null;
}

/** The screen in four sentences: what was asked, how many answered, what to look at first. */
function FeedbackReading({ summary, question, negatives, portalLink }: FeedbackReadingProps) {
  const reliable = summary.byAnnouncement.filter((row) => !row.lowConfidence);
  const toReview = [...reliable].sort((a, b) => b.demandScore - a.demandScore)[0];
  const clearest = [...reliable].sort((a, b) => a.negativeShare - b.negativeShare)[0];

  const rows: ReadingRow[] = [
    {
      label: 'Question posée',
      content: (
        <>
          {question ? <strong>« {question} »</strong> : 'Une question fermée'}
          <span className="reading-list__sub">
            Proposée à l'usager sur les pages d'annonce, après lecture, une seule fois par annonce.
          </span>
        </>
      ),
    },
    {
      label: 'Participation',
      content: (
        <>
          <strong>{count(summary.responses)}</strong> avis pour {count(summary.promptsShown)} questions
          affichées, soit {percent(summary.participationRate)}.
          <span className="reading-list__sub">
            {percent(summary.negativeShare)} des réponses sont {negatives}.
          </span>
        </>
      ),
    },
  ];

  if (toReview) {
    const link = portalLink(toReview);
    rows.push({
      label: 'La moins claire',
      content: (
        <>
          <strong>{announcementName(toReview.key)}</strong> : {percent(toReview.negativeShare)} de réponses{' '}
          {negatives} sur {count(toReview.responses)} avis.
          <span className="reading-list__sub">
            L'annonce la moins claire pour les usagers, parmi celles qui ont assez d'avis.
          </span>
        </>
      ),
      visual: <ScoreChip score={toReview.demandScore} />,
      ...(link ? { action: { label: 'Voir sur le portail', to: link } } : {}),
    });
  }
  if (clearest && clearest.key !== toReview?.key) {
    rows.push({
      label: 'La plus claire',
      content: (
        <>
          <strong>{announcementName(clearest.key)}</strong> : {percent(clearest.negativeShare)} de réponses{' '}
          {negatives} sur {count(clearest.responses)} avis.
          <span className="reading-list__sub">Un modèle de rédaction pour les autres annonces.</span>
        </>
      ),
      visual: <ScoreChip score={clearest.demandScore} />,
    });
  }

  return (
    <div data-testid="feedback-reading">
      <Card title="En résumé" className="feedback__section">
        <ReadingList rows={rows} />
      </Card>
    </div>
  );
}

function ScoreChip({ score }: { score: number }) {
  return (
    <span className="feedback__score">
      <MiniBar value={score / 100} tone={score >= DEMAND_ALERT ? 'negative' : 'brand'} />
      <span>
        Score de demande <strong>{score}</strong>
      </span>
    </span>
  );
}
